import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type {
  DashboardChannelUsage,
  DashboardSparklinePoint,
  DashboardSummary,
} from "@chat-yai/shared/admin-types";
import { dashboardApi } from "@/api/endpoints/dashboard";
import { Icon } from "@/components/Icon";
import { EmptyState } from "@/components/EmptyState";

// Real dashboard (WP-C, U-C1). Hooks GET /admin/dashboard/summary, renders
// 4 KPI cards, a 7-day SVG sparkline, the plan-channel usage block, the
// 3-step onboarding card (only when chatbots == 0), and a recent-activity
// list reused from WP-A.
export function DashboardPage(): JSX.Element {
  const summaryQuery = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => dashboardApi.summary(),
    staleTime: 30_000,
  });

  const data = summaryQuery.data;

  return (
    <div className="page" data-testid="dashboard-page">
      <div className="page-head">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">
            Today&apos;s conversations, plan usage, and recent activity at a glance.
          </div>
        </div>
      </div>

      {summaryQuery.isLoading ? (
        <div className="empty">Loading dashboard…</div>
      ) : !data ? (
        <EmptyState
          title="Could not load dashboard"
          description="Refresh the page or sign out and back in."
          icon="alert"
        />
      ) : (
        <>
          {data.chatbots_count === 0 ? <OnboardingCard summary={data} /> : null}

          <div className="kpi-grid">
            <KpiCard
              label="Chats today"
              value={data.chats_today.toLocaleString()}
              meta={data.chats_today === 0 ? "No conversations yet today" : "across all channels"}
              testId="kpi-chats-today"
            />
            <KpiCard
              label="Last 7 days"
              value={data.chats_7d.toLocaleString()}
              meta="conversations"
              testId="kpi-chats-7d"
            />
            <KpiCard
              label="Unread leads"
              value={data.unread_leads.toLocaleString()}
              meta={data.unread_leads === 0 ? "Inbox zero" : "marked 'new'"}
              testId="kpi-unread-leads"
            />
            <KpiCard
              label="Plan"
              value={data.current_plan.name}
              meta={data.current_plan.code}
              testId="kpi-current-plan"
              compact
            />
          </div>

          <div className="col-2" style={{ marginTop: "var(--s-6)" }}>
            <div className="card">
              <div className="sect-head">
                <h3>7-day trend</h3>
                <span className="help">Conversations per day</span>
              </div>
              <div style={{ padding: "var(--s-5)" }}>
                <Sparkline points={data.trend_7d} />
              </div>
            </div>
            <div className="card">
              <div className="sect-head">
                <h3>Plan usage</h3>
                <Link to="/profile" className="help">
                  Manage plan
                </Link>
              </div>
              <div style={{ padding: "var(--s-3) var(--s-5)" }}>
                {data.channel_usage.map((u) => (
                  <UsageRow key={u.channel} usage={u} />
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: "var(--s-6)" }}>
            <div className="sect-head">
              <h3>Recent activity</h3>
              <Link to="/audit-logs" className="help">
                View full log
              </Link>
            </div>
            {data.recent_activity.length === 0 ? (
              <EmptyState
                title="Nothing here yet"
                description="Audit events appear when teammates create chatbots, edit prompts, or invite members."
                icon="history"
              />
            ) : (
              <div>
                {data.recent_activity.map((a) => (
                  <div
                    className="list-row"
                    key={a.id}
                    style={{ gridTemplateColumns: "1fr auto" }}
                    data-testid={`activity-row-${a.id}`}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="audit-action">{a.action}</div>
                      <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                        {a.actor_email ?? "system"} · {new Date(a.created_at).toLocaleString()}
                      </div>
                    </div>
                    {a.target_type ? <span className="chip">{a.target_type}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  meta: string;
  testId?: string;
  compact?: boolean;
}
function KpiCard({ label, value, meta, testId, compact }: KpiCardProps): JSX.Element {
  return (
    <div className="kpi" data-testid={testId}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ fontSize: compact ? 22 : 30 }}>
        {value}
      </div>
      <div className="kpi-meta mono">{meta}</div>
    </div>
  );
}

// 7-bucket SVG sparkline. Hand-rolled (no recharts) — keeps bundle slim and
// matches the design prototype's aesthetic. Width is fluid via viewBox.
function Sparkline({ points }: { points: DashboardSparklinePoint[] }): JSX.Element {
  const W = 320;
  const H = 96;
  const pad = 8;
  const maxY = Math.max(1, ...points.map((p) => p.count));
  const stepX = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => ({
    x: pad + i * stepX,
    y: H - pad - (p.count / maxY) * (H - pad * 2),
    p,
  }));
  const linePath = coords
    .map((c, i) => (i === 0 ? `M ${c.x} ${c.y}` : `L ${c.x} ${c.y}`))
    .join(" ");
  const areaPath =
    coords.length > 0
      ? `M ${coords[0].x} ${H - pad} ${coords
          .map((c) => `L ${c.x} ${c.y}`)
          .join(" ")} L ${coords[coords.length - 1].x} ${H - pad} Z`
      : "";

  return (
    <svg
      className="dashboard-trend"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="7-day conversation trend"
      data-testid="dashboard-sparkline"
    >
      <path d={areaPath} className="area" />
      <path d={linePath} className="line" />
      {coords.map((c) => (
        <circle key={c.p.date} cx={c.x} cy={c.y} r={2.5} className="point" />
      ))}
      {coords.map((c, i) =>
        i === 0 || i === coords.length - 1 ? (
          <text
            key={`l-${c.p.date}`}
            x={c.x}
            y={H - 1}
            textAnchor={i === 0 ? "start" : "end"}
            className="label"
          >
            {new Date(c.p.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </text>
        ) : null,
      )}
    </svg>
  );
}

const CHANNEL_LABEL: Record<string, string> = {
  chat: "Chat",
  web_voice: "Web voice",
  phone: "Phone",
};

function UsageRow({ usage }: { usage: DashboardChannelUsage }): JSX.Element {
  const enabled = usage.limit !== null;
  const pct = useMemo(() => {
    if (!enabled || !usage.limit || usage.used == null) return 0;
    return Math.min(100, Math.round((usage.used / usage.limit) * 100));
  }, [enabled, usage]);

  return (
    <div className="usage-row" data-testid={`usage-${usage.channel}`}>
      <span className="usage-channel">
        <span
          className="chip-dot"
          style={{
            background:
              usage.channel === "chat"
                ? "var(--ch-chat)"
                : usage.channel === "web_voice"
                  ? "var(--ch-voice)"
                  : "var(--ch-phone)",
          }}
        />
        {CHANNEL_LABEL[usage.channel] ?? usage.channel}
      </span>
      {enabled ? (
        <div className="usage-bar" aria-label={`${pct}% used`}>
          <div
            className={"usage-bar-fill" + (pct >= 90 ? " over" : "")}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <span className="usage-disabled">Not enabled on this plan</span>
      )}
      {enabled ? (
        <span className="usage-meta">
          {(usage.used ?? 0).toLocaleString()} / {usage.limit?.toLocaleString()} {usage.unit}
        </span>
      ) : (
        <span className="usage-meta">—</span>
      )}
    </div>
  );
}

function OnboardingCard({ summary }: { summary: DashboardSummary }): JSX.Element {
  const totalDone = summary.onboarding.filter((s) => s.done).length;
  return (
    <div className="card" style={{ marginBottom: "var(--s-6)" }} data-testid="onboarding-card">
      <div className="sect-head">
        <h3>Get your widget live in 3 steps</h3>
        <span className="chip chip-accent">
          {totalDone} / {summary.onboarding.length} done
        </span>
      </div>
      <div className="onboarding-steps" style={{ padding: "var(--s-4) var(--s-5)" }}>
        {summary.onboarding.map((step, i) => (
          <Link
            key={step.id}
            to={step.href as "/sites"}
            className={"onboarding-step" + (step.done ? " done" : "")}
            data-testid={`onboarding-step-${step.id}`}
          >
            <span className="num">{step.done ? <Icon name="check" size={14} /> : i + 1}</span>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{step.label}</div>
              <div className="help" style={{ marginTop: 2 }}>
                {step.done ? "Done" : `Next step`}
              </div>
            </div>
            <Icon name="chevron-right" size={14} />
          </Link>
        ))}
      </div>
    </div>
  );
}
